// Migration service to move JSON files to IndexedDB
import databaseService from './databaseService';

class MigrationService {
  constructor() {
    this.jsonFiles = [
      'about', 'advice', 'bulk-email', 'coding', 'home', 'latika', 
      'links', 'love', 'mental-junk', 'pageContent', 'parenting', 
      'persuasion', 'productive', 'sagu', 'url-shortner', 'why'
    ];
  }

  async migrateAllJSONFiles() {
    try {
      await databaseService.init();
      console.log('Starting migration from JSON to IndexedDB...');

      for (const fileName of this.jsonFiles) {
        try {
          const module = await import(`../data/${fileName}.json`);
          const jsonData = module.default;
          
          // Convert JSON structure to database format
          const postData = this.convertJSONToPost(jsonData, fileName);
          
          // Check if post already exists
          const existingPost = await databaseService.getPostByRoute(postData.route);
          if (!existingPost) {
            await databaseService.createPost(postData);
            console.log(`Migrated: ${fileName}.json`);
          } else {
            console.log(`Skipped: ${fileName}.json (already exists)`);
          }
        } catch (error) {
          console.error(`Failed to migrate ${fileName}.json:`, error);
        }
      }

      console.log('Migration completed!');
      return true;
    } catch (error) {
      console.error('Migration failed:', error);
      return false;
    }
  }

  convertJSONToPost(jsonData, fileName) {
    // Convert JSON structure to database post format
    const route = this.generateRouteFromFileName(fileName);
    
    return {
      title: jsonData.name || fileName.charAt(0).toUpperCase() + fileName.slice(1),
      description: jsonData.description || '',
      content: jsonData,
      route: route,
      date: new Date().toISOString(),
      requiresAuth: this.isProtectedRoute(fileName),
      isHidden: this.isHiddenRoute(fileName),
      sections: jsonData.sections || []
    };
  }

  generateRouteFromFileName(fileName) {
    // Generate route based on file name
    if (fileName === 'home') return '/';
    if (fileName === 'latika') return '/f/latika';
    if (fileName === 'why') return '/page/why-i-do-what-i-do';
    return `/${fileName}`;
  }

  isProtectedRoute(fileName) {
    // Define which routes should be protected
    const protectedRoutes = ['latika', 'secrets', 'notes'];
    return protectedRoutes.includes(fileName);
  }

  isHiddenRoute(fileName) {
    // Define which routes should be hidden
    const hiddenRoutes = ['parenting', 'mental-junk', 'write'];
    return hiddenRoutes.includes(fileName);
  }

  async createSampleArchivePosts() {
    try {
      await databaseService.init();
      
      const sampleArchives = [
        // 2024 posts
        {
          title: "Reflecting on 2024",
          content: "Looking back at the year that was...",
          excerpt: "A year of growth, challenges, and new opportunities.",
          route: "/archives/reflecting-on-2024",
          year: 2024,
          month: 12,
          date: "2024-12-15"
        },
        {
          title: "Building Better Habits",
          content: "Thoughts on habit formation and consistency...",
          excerpt: "How small changes can lead to big transformations.",
          route: "/archives/building-better-habits",
          year: 2024,
          month: 11,
          date: "2024-11-20"
        },
        {
          title: "Learning New Technologies",
          content: "Exploring the latest in web development...",
          excerpt: "My journey with new frameworks and tools.",
          route: "/archives/learning-new-technologies",
          year: 2024,
          month: 10,
          date: "2024-10-10"
        },
        {
          title: "Summer Productivity",
          content: "Making the most of the summer months...",
          excerpt: "Tips for staying productive during vacation time.",
          route: "/archives/summer-productivity",
          year: 2024,
          month: 8,
          date: "2024-08-15"
        },
        // 2023 posts
        {
          title: "Year End Reflections",
          content: "What I learned in 2023...",
          excerpt: "Key lessons and insights from the past year.",
          route: "/archives/year-end-reflections-2023",
          year: 2023,
          month: 12,
          date: "2023-12-31"
        },
        {
          title: "Career Changes",
          content: "Thoughts on switching career paths...",
          excerpt: "The challenges and rewards of career transitions.",
          route: "/archives/career-changes",
          year: 2023,
          month: 9,
          date: "2023-09-15"
        },
        // 2022 posts
        {
          title: "Remote Work Insights",
          content: "Lessons learned from working remotely...",
          excerpt: "How remote work changed my perspective on productivity.",
          route: "/archives/remote-work-insights",
          year: 2022,
          month: 6,
          date: "2022-06-20"
        },
        {
          title: "Starting a New Project",
          content: "The excitement and challenges of new beginnings...",
          excerpt: "What I learned from starting my latest project.",
          route: "/archives/starting-new-project",
          year: 2022,
          month: 3,
          date: "2022-03-10"
        },
        // 2021 posts
        {
          title: "Pandemic Reflections",
          content: "How the pandemic changed everything...",
          excerpt: "Personal thoughts on adapting to new realities.",
          route: "/archives/pandemic-reflections",
          year: 2021,
          month: 12,
          date: "2021-12-01"
        },
        {
          title: "Learning to Code",
          content: "My journey into programming...",
          excerpt: "The ups and downs of learning to code.",
          route: "/archives/learning-to-code",
          year: 2021,
          month: 8,
          date: "2021-08-15"
        },
        // 2020 posts
        {
          title: "Fresher's Guide to getting a Job",
          content: "A comprehensive guide for fresh graduates on how to land their first job...",
          excerpt: "Essential tips and strategies for new graduates entering the job market.",
          route: "/archives/freshers-guide-to-getting-a-job",
          year: 2020,
          month: 10,
          date: "2020-10-21"
        },
        {
          title: "First Blog Post",
          content: "Welcome to my blog...",
          excerpt: "The beginning of my blogging journey.",
          route: "/archives/first-blog-post",
          year: 2020,
          month: 12,
          date: "2020-12-01"
        }
      ];

      for (const archive of sampleArchives) {
        const existing = await databaseService.getArchiveByRoute(archive.route);
        if (!existing) {
          await databaseService.createArchivePost(archive);
          console.log(`Created archive post: ${archive.title}`);
        } else {
          console.log(`Archive post already exists: ${archive.title}`);
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to create sample archive posts:', error);
      return false;
    }
  }

  async forceRefreshArchives() {
    try {
      await databaseService.init();
      console.log('Force refreshing archives...');
      await this.createSampleArchivePosts();
      return true;
    } catch (error) {
      console.error('Failed to force refresh archives:', error);
      return false;
    }
  }
}

// Create a singleton instance
const migrationService = new MigrationService();

export default migrationService;
